import React, { useEffect, useState } from "react"
import { FormTitle } from "./Title";
import styles from './Form.module.scss';
import { FormGroup } from "./FormGroup";
import { Checkbox } from "./FormGroup/Checkbox";
import { Button } from "./Button";
import { useForm } from 'react-hook-form';
import { LegalText } from "../LegalText";
import { ConditionalRender } from "../ConditionalRender";
import { capitalize } from "@/utils/string";
import { getFormInstanceValues } from "@/utils/form";
import { sendDigestMessage } from "@/utils/customTagBlueKai";
import classNames from "classnames";
import { maskPhone, maskPlate, maskVIN } from "@/utils/masks";

const remapFieldNamesToSalesForceIds = (formData: FormDataInterface) => {
  const fieldsMap: Record<string, string> = {
    name: "00NU0000003Hgr4",
    email: "00NU0000003HgrJ",
    phone: "00NU0000003HgrO",
    model: "00NU00000063rMN",
    plate: "00NU0000003Hgpc",
    chassi: "00NU0000003Hgqz",
    acceptedTerms: "00N6f00000FhO7k",
    acceptedTermsHidden: "00N6f00000FhO8i",
    acceptMarketing: "00N6f00000FhO8d",
    acceptMarketingHidden: "00N6f00000FhO8n",
  };

  const form = new FormData();
  Object
    .entries(formData)
    .forEach(([key, value]) => {
      const fieldMapKeyValue = fieldsMap[key] || key;
      if (key === 'model') {
        value = value.replace('null', '')
      }
      form.set(fieldMapKeyValue, value);
    });

  return form;
}

export interface FormDataInterface {
  external: string;
  orgid?: string;
  // name
  "00NU0000003Hgr4": string;
  // email
  "00NU0000003HgrJ": string;
  // phone
  "00NU0000003HgrO": string;
  // model
  "00NU00000063rMN": string;
  //plate
  "00NU0000003Hgpc": string;
  // chassi
  "00NU0000003Hgqz": string;
  subject: string;
  description: string;
  // FormNewsletterGroup
  // acceptedTerms
  "00N6f00000FhO7k": string;
  // acceptedTermsHidden
  "00N6f00000FhO8i": string;

  // FormInformativeGroup
  // acceptMarketing
  "00N6f00000FhO8d": string;
  // acceptMarketingHidden
  "00N6f00000FhO8n": string;
}

export const Form: React.FC = () => {
  const [userName, setUsername] = useState('');
  const {
    register,
    handleSubmit,
    watch,
    formState: {
      errors,
      isValid,
      isSubmitting,
      isSubmitSuccessful,
    },
  } = useForm<FormDataInterface>({
    mode: 'all',
    defaultValues: {
      external: "1",
      orgid: "",
      "00NU00000063rMN": "null",
      // FormNewsletterGroup
      "00N6f00000FhO8i": "Declaro que li e estou de acordo com a Política de Privacidade da Nissan do Brasil.",

      // FormInformativeGroup
      "00N6f00000FhO8n": "Eu aceito receber informativos da Nissan, seus parceiros/concessionárias e do Banco RCI/CrediNissan, por meio dos meios de comunicação informados por mim (telefone / WhatsApp e/ou e-mail), seja para divulgação de ofertas, anúncios de produtos e serviços, Ações de Marca da Nissan, sua rede de concessionárias, Banco RCI/CrediNissan, Newsletter e sobre produtos financeiros.",
    }
  });

  const formUserName = watch('00NU0000003Hgr4');

  useEffect(() => {
    setUsername(formUserName?.split(' ')[0]);
  }, [formUserName]);

  const onSubmit = (data: FormDataInterface, event: any) => {
    if (!isValid) {
      return false;
    }

    event.target.querySelector('input[name="orgid"]').value = '00DU0000000KiGE';
    event.target.querySelector('input[name="00N6f00000FhO7k"]').value = 'true';

    // Accept Marketing ?
    const acceptMarketing = event.target.querySelector('input[name="00N6f00000FhO8d"]').checked;
    event.target.querySelector('input[name="00N6f00000FhO8d"]').value = acceptMarketing.toString();
    if (!acceptMarketing) {
      event.target.querySelector('input[name="00N6f00000FhO8d"]').type = 'hidden'
    }
    // /Accept Marketing ?

    event.target.action = "https://webto.salesforce.com/servlet/servlet.WebToCase?encoding=UTF-8"
    // event.target.action = "https://webhook.site/ae892dbd-b789-43f2-8246-412128727456"
    // event.target.action = "https://localhost:3000"
    event.target.method = "POST";

    setUsername(data["00NU0000003Hgr4"].split(' ')[0].trim());

    const remapedFormFields = remapFieldNamesToSalesForceIds(data);
    const { CustomerIdentificationNumber__c } = getFormInstanceValues(remapedFormFields);
    sendDigestMessage(CustomerIdentificationNumber__c);
    event.target.submit();
  };

  return (
    <div>
      <iframe
        name="hiddenFrame"
        style={{
          display: "none",
          border: 0,
          width: 0,
          height: 0,
        }}
      ></iframe>

      <ConditionalRender condition={!isSubmitSuccessful}>
        <div className={classNames(styles.formContainer, {
          [styles.isSubmitting]: isSubmitting,
        })}>
          <div className={classNames({
            [styles.loader]: isSubmitting
          })} />
          <FormTitle />
          <form
            id="form_sac"
            className={classNames(styles.Form, {
              [styles.isSubmitting]: isSubmitting,
            })}
            onSubmit={handleSubmit(onSubmit)}
            target="hiddenFrame"
          >
            <input
              type="hidden"
              id="external"
              {...register("external", {
                required: true,
              })}
            />
            <input
              type="hidden"
              id="orgid"
              {...register("orgid")}
            />

            {/* <input type=hidden name="retURL" value="https://www.nissan-cdn.net/content/dam/Nissan/br/forms/sucesso-faleconosco.html">  */}
            {/* NOTE: These fields are optional debugging elements. Please uncomment     */}
            {/* these lines if you wish to test in debug mode.                           */}
            {/* <input type="hidden" name="debug" value=1>                               */}
            {/* <input type="hidden" name="debugEmail" value="tony@kolekto.com.br">      */}
            {/* ----------------------------------------------------------------------   */}
            <FormGroup
              label={{
                htmlFor: "name",
                text: "Nome Completo",
              }}
              input={{
                id: "name",
                maxLength: 40,
                type: "text",
                required: true,
                ...register("00NU0000003Hgr4", {
                  required: 'Por favor, insira seu nome e sobrenome',
                  validate: {
                    nameComplete: (value) => {
                      if (value.trim().split(' ').length < 2) {
                        return 'Por favor, insira seu nome e sobrenome'
                      }
                      return true;
                    }
                  }
                }),
              }}
              error={errors["00NU0000003Hgr4"]?.message}
            />

            <FormGroup
              label={{
                htmlFor: "email",
                text: "E-mail",
              }}
              input={{
                id: "email",
                maxLength: 80,
                size: 20,
                type: "email",
                required: true,
                placeholder: "seunome@dominio.com.br",
                ...register("00NU0000003HgrJ", {
                  required: 'Por favor, insira seu e-mail',
                  pattern: {
                    value: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                    message: 'Por favor, insira um e-mail válido',
                  },
                }),
              }}
              error={errors["00NU0000003HgrJ"]?.message}
            />

            <FormGroup
              label={{
                htmlFor: "phone",
                text: "Telefone de contato",
              }}
              input={{
                placeholder: "(00) 00000-0000",
                ...register("00NU0000003HgrO", {
                  onChange(event) {
                    event.target.value = maskPhone(event.target.value);
                  },
                  validate: {
                    validatePhone: (value) => {
                      const valueLength = value.replace(/\D*/g, '').length;
                      if (valueLength > 0 && valueLength < 10) {
                        return 'Por favor, insira um telefone de contato válido';
                      }
                      return true;
                    }
                  }
                }),
              }}
              error={errors["00NU0000003HgrO"]?.message}
            />

            <FormGroup
              label={{
                htmlFor: 'model',
                text: 'Modelo do veículo*',
              }}
              select={{
                id: "model",
                ...register("00NU00000063rMN", {
                  validate: {
                    required: (value) => {
                      if (value === 'null' || value === null) {
                        return 'Por favor, selecione um modelo'
                      }
                      return true;
                    }
                  }
                }),
              }}
              selectOptions={[
                { value: "null", disabled: true, text: "Selecione um modelo" },
                { value: "", text: "-- Nenhum --" },
                { value: "Nissan Kicks", text: "Nissan Kicks" },
                { value: "Altima", text: "Altima" },
                { value: "Frontier", text: "Frontier" },
                { value: "Grand Livina", text: "Grand Livina" },
                { value: "Livina", text: "Livina" },
                { value: "Livina X-Gear", text: "Livina X-Gear" },
                { value: "March", text: "March" },
                { value: "Sentra", text: "Sentra" },
                { value: "New March", text: "New March" },
                { value: "Tiida Hatch", text: "Tiida Hatch" },
                { value: "Tiida Sedan", text: "Tiida Sedan" },
                { value: "Versa", text: "Versa" },
                { value: "350Z", text: "350Z" },
                { value: "370Z", text: "370Z" },
                { value: "Pathfinder", text: "Pathfinder" },
                { value: "Murano", text: "Murano" },
                { value: "X-Terra", text: "X-Terra" },
                { value: "X-Trail", text: "X-Trail" },
                { value: "Não se aplica a veículo", text: "Não se aplica a veículo" },
                { value: "GTR", text: "GTR" },
                { value: "Leaf", text: "Leaf" },
                { value: "Não comercializado no Brasil", text: "Não comercializado no Brasil" },
              ]}
              error={errors["00NU00000063rMN"]?.message}
            />

            <FormGroup
              label={{
                htmlFor: "plate",
                text: "Placa do veículo",
              }}
              input={{
                placeholder: "ABC-1B34",
                ...register("00NU0000003Hgpc", {
                  onChange(event) {
                    event.target.value = maskPlate(event.target.value);
                  },
                  validate: {
                    validatePlate: (value) => {
                      const valueLength = value.replace(/\W*/, '').length;

                      if (
                        valueLength > 0
                        && !value.match(/[a-z]{3}\-[0-9][a-z0-9][0-9]{2}/i)
                      ) {
                        return 'Por favor, insira uma placa válida'
                      }
                      return true;
                    }
                  }
                })
              }}
              error={errors["00NU0000003Hgpc"]?.message}
            />

            <FormGroup
              label={{
                htmlFor: "chassi",
                text: "Chassi do veículo",
              }}
              input={{
                ...register("00NU0000003Hgqz", {
                  onChange(event) {
                    event.target.value = maskVIN(event.target.value);
                  },
                  min: {
                    value: 17,
                    message: 'Por favor, insira um chassi válido'
                  }
                }),
              }}
              error={errors["00NU0000003Hgqz"]?.message}
            />

            <FormGroup
              label={{
                htmlFor: 'subject',
                text: 'Assunto',
              }}
              select={{
                id: "subject",
                required: true,
                defaultValue: '',
                ...register("subject", {
                  required: 'Por favor, selecione um assunto',
                }),
              }}
              selectOptions={[
                { value: "", disabled: true, text: "Selecione um assunto" },
                { value: "Informação", text: "Informação" },
                { value: "Reclamação", text: "Reclamação" },
                { value: "Solicitação", text: "Solicitação" },
              ]}
              error={errors.subject?.message}
            />

            <FormGroup
              label={{
                htmlFor: "description",
                text: "Mensagem",
              }}
              textArea={{
                id: "description",
                maxLength: 400,
                required: true,
                ...register("description", {
                  required: 'Por favor, insira sua mensagem ou dúvida',
                }),
              }}
              error={errors.description?.message}
            />

            <Checkbox
              hidden={{
                id: "acceptedTermsHidden",
                ...register("00N6f00000FhO8i", {
                  required: "Você precisa aceitar a Política de Privacidade para continuar",
                }),
              }}
              label={{
                text: `Declaro que li e estou de acordo com a <a href="https://www.nissan.com.br/politica-de-privacidade.html" target="_blank">Política de Privacidade</a> da Nissan do Brasil.*`,
                htmlFor: "acceptedTerms",
              }}
              input={{
                type: "checkbox",
                id: "acceptedTerms",
                ...register("00N6f00000FhO7k", {
                  required: "Você precisa aceitar a Política de Privacidade para continuar",
                }),
              }}
              error={errors["00N6f00000FhO7k"]?.message}
            />

            <Checkbox
              hidden={{
                id: "acceptMarketingHidden",
                ...register("00N6f00000FhO8n", {
                  required: "Você precisa aceitar a os informativos da Nissan para continuar",
                }),
              }}
              label={{
                text: `Eu aceito receber informativos da Nissan, seus parceiros/ concessionárias e do Banco RCI/CrediNissan, por meio dos meios de comunicação informados por mim (telefone / WhatsApp e/ou e-mail), seja para divulgação de ofertas, anúncios de produtos e serviços, Ações de Marca da Nissan, sua rede de concessionárias, Banco RCI/CrediNissan, Newsletter e sobre produtos financeiros.`,
                htmlFor: "acceptMarketing",
              }}
              input={{
                type: "checkbox",
                id: "acceptMarketing",
                ...register("00N6f00000FhO8d"),
              }}
              error={errors["00N6f00000FhO8d"]?.message}
            />
            <Button />
          </form>
        </div>
      </ConditionalRender>

      <ConditionalRender condition={isSubmitSuccessful}>
        <div className={styles.submitSuccess}>
          <span className={styles.submitSuccessMessage}>
            <span className={styles.submitSuccessMessageUserName}>
              {userName
                ? `${capitalize(userName)}, a`
                : 'A'
              }
            </span>
            <span> Nissan agradece seu contato</span>
          </span>
          <span className={styles.submitMessageSuccess}>
            Em breve retornaremos sua mensagem
          </span>
        </div>
      </ConditionalRender>

      <LegalText />
    </div>
  );
}